import React from 'react';
import { getIntl } from '../../../../../utils/intl';
import { MedicalDataTypes, APITypes } from '../../../../../primary-record-types';
import useMedicalProfileData from '../../../../../hooks/api/useMedicalProfileData';
import { PromptActions, PromptContainer } from '../../../../v2BaseComponents/Prompt';
import Stack, { contentStackSpacing } from '../../../../v2BaseComponents/Stack';
import Text from '../../../../v2BaseComponents/Text';
import { getSpacing } from '../../../../../utils/spacing';

const intl = getIntl('EditVisitSeriesPrompt', {
    title: 'Edit Recurring Visit',
    body: 'This visit is part of a recurring series. Apply your changes to:',
    thisEvent: 'This event only',
    thisAndFollowing: 'This and all future events',
    allEvents: 'All events',
    cancel: 'Cancel',
});

/**
 * Scope options for editing a visit that belongs to a recurring series. "This and future"
 * and "All" propagate the edited fields to the matching occurrences, each keeping its own
 * date (only the new time-of-day carries over). Changing the repeat pattern itself is not
 * supported here — delete the series and create a new one for that.
 */
const EditVisitSeriesPrompt: React.FC<{
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    profileId: string;
    visit: MedicalDataTypes.PrimaryRecordVisit;
    /** The edited visit fields, captured when the user saved the form. */
    pendingResourceUpdate: Partial<MedicalDataTypes.PrimaryRecordVisit> | null;
    onUpdateSingle: () => void;
    onSeriesUpdated: () => void;
}> = ({ isOpen, setIsOpen, profileId, visit, pendingResourceUpdate, onUpdateSingle, onSeriesUpdated }) => {
    const { updateVisitSeries, isUpdatingVisitSeries } = useMedicalProfileData({
        medicalProfileId: profileId,
        resourceCategory: 'visit',
        onBulkUpdateSuccess: () => {
            setIsOpen(false);
            onSeriesUpdated();
        },
    });

    // Series scopes propagate field edits only; image add/remove applies through "This event"
    // (the single-visit update). Existing images on each occurrence are preserved, so this is
    // never destructive - only new image edits are not carried across the series.
    const applyScope = (scope: APITypes.medicalDataService.UpdateVisitSeriesScope) => {
        if (!visit.recurrenceGroupId || !visit.startDateTime || !pendingResourceUpdate) return;
        updateVisitSeries({
            recurrenceGroupId: visit.recurrenceGroupId,
            scope,
            fromDateTime: visit.startDateTime,
            resourceUpdate: pendingResourceUpdate,
        });
    };

    return (
        <PromptContainer
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            renderPromptContents={() => (
                <Stack vertical fullWidth space={contentStackSpacing} padding={getSpacing(6)}>
                    <Text variant="h4">{intl('title')}</Text>
                    <Text variant="body-1">{intl('body')}</Text>
                    <PromptActions
                        actions={[
                            {
                                title: intl('thisEvent'),
                                onPress: () => {
                                    setIsOpen(false);
                                    onUpdateSingle();
                                },
                                isDisabled: isUpdatingVisitSeries,
                                testID: 'edit-visit-series-this',
                            },
                            {
                                title: intl('thisAndFollowing'),
                                onPress: () => applyScope('following'),
                                isDisabled: isUpdatingVisitSeries,
                                testID: 'edit-visit-series-future',
                            },
                            {
                                title: intl('allEvents'),
                                onPress: () => applyScope('all'),
                                isDisabled: isUpdatingVisitSeries,
                                testID: 'edit-visit-series-all',
                            },
                            {
                                title: intl('cancel'),
                                variant: 'secondary',
                                onPress: () => setIsOpen(false),
                                testID: 'edit-visit-series-cancel',
                            },
                        ]}
                    />
                </Stack>
            )}
        />
    );
};

export default React.memo(EditVisitSeriesPrompt);
